// pages/SbcTaxTables.jsx — Admin Board tab (kailux-fixes-log.md §104)
//
// Small Business Corporation (SBC) income-tax tables, stored in the database and keyed by the
// company's YEAR-END range. Add each new year's table here when SARS publishes it. Every save is
// validated on the server (bands ascend, rates 0-1, each printed base matches its band formula,
// no overlap or gap between year-end ranges) and audited — nothing is silently overwritten.
// A coverage banner warns before the latest table runs out.
import { useState, useEffect } from 'react';
import { getSbcTables, addSbcTable, editSbcTable, supersedeSbcTable } from '../api/kailux';
import Spinner from '../components/Spinner';
import {
  bandsToRows, rowsToBands, suggestBases, nextYearDefaults, summariseBands, errorText,
} from '../utils/sbcTables';
import { T } from '../theme/theme'

const TONE = {
  ok: { c: T.gn, bg: T.gnd }, warning: { c: T.am, bg: T.amd }, critical: { c: T.rd, bg: T.rdd },
};
const input = { width:'100%', marginTop:'4px', padding:'8px', background:T.s3, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color:T.tx, fontSize:'13px', boxSizing:'border-box' };
const btn = (primary) => ({ padding:'8px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:700, cursor:'pointer',
  border: primary ? 'none' : `1px solid ${T.bd}`, background:primary ? T.prg : 'transparent', color: primary ? T.onpri : T.mt });

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-ZA', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch { return iso; }
}

export function CoverageBanner({ coverage, onAdd }) {
  if (!coverage || coverage.level === 'ok') return null;
  const tone = TONE[coverage.level] || TONE.warning;
  return (
    <div data-testid="sbc-coverage-banner" role="alert"
      style={{ background:tone.bg, border:`1px solid ${tone.c}`, borderRadius:'12px', padding:'14px 16px', marginBottom:'18px',
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:'12px', fontWeight:800, color:tone.c, textTransform:'uppercase', letterSpacing:'0.06em' }}>
          {coverage.level === 'critical' ? 'SBC tax tables — action needed' : 'SBC tax tables — running out'}
        </div>
        <div style={{ fontSize:'13px', color:T.tx, marginTop:'4px' }}>{coverage.message}</div>
      </div>
      {onAdd && <button type="button" onClick={onAdd} style={btn(true)}>Add the next table</button>}
    </div>
  );
}

const blankForm = () => ({
  mode: 'add', id: null, ends_from: '', ends_to: '', source: '', notes: '',
  rows: bandsToRows([
    { lower: 0, base: 0, rate: 0 }, { lower: 0, base: 0, rate: 0.07 },
    { lower: 365000, base: 0, rate: 0.21 }, { lower: 550000, base: 0, rate: 0.27 },
  ]),
  confirmed: false, prefilled: false,
});

export default function SbcTaxTables({ onCoverageChanged }) {
  const [data, setData] = useState({ tables: [], coverage: null, conditions: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [notice, setNotice] = useState('');
  const [historyId, setHistoryId] = useState(null);
  const [supersedeId, setSupersedeId] = useState(null);
  const [supersedeReason, setSupersedeReason] = useState('');
  const [supersedeError, setSupersedeError] = useState('');

  const load = () => {
    setLoading(true);
    setLoadError('');
    return getSbcTables()
      .then((d) => { setData(d); if (onCoverageChanged) onCoverageChanged(d.coverage); })
      .catch((err) => setLoadError(errorText(err, 'Could not load the SBC tax tables.')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startAdd = () => {
    const f = blankForm();
    const next = nextYearDefaults(data.tables);
    if (next.ends_from) {
      f.ends_from = next.ends_from; f.ends_to = next.ends_to;
      if (next.bands) { f.rows = bandsToRows(next.bands); f.prefilled = true; }
    }
    setForm(f); setFormErrors([]); setNotice('');
  };

  const startEdit = (t) => {
    setForm({ mode: 'edit', id: t.id, ends_from: t.ends_from, ends_to: t.ends_to, source: t.source || '',
      notes: t.notes || '', rows: bandsToRows(t.bands), confirmed: false, prefilled: false });
    setFormErrors([]); setNotice('');
  };

  const setRow = (i, key, value) => setForm((f) => ({ ...f, rows: f.rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)) }));
  const addRow = () => setForm((f) => ({ ...f, rows: [...f.rows, { lower: '', base: '', ratePct: '' }] }));
  const removeRow = (i) => setForm((f) => (f.rows.length > 2 ? { ...f, rows: f.rows.filter((_, j) => j !== i) } : f));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.confirmed) { setFormErrors(['Tick the box to confirm you checked every figure against the source page.']); return; }
    const { bands, error } = rowsToBands(form.rows);
    if (error) { setFormErrors([error]); return; }
    if (!form.ends_from || !form.ends_to) { setFormErrors(['Both year-end dates are required.']); return; }
    setSaving(true); setFormErrors([]);
    try {
      const payload = { endsFrom: form.ends_from, endsTo: form.ends_to, bands, source: form.source.trim(), notes: form.notes };
      const result = form.mode === 'add' ? await addSbcTable(payload) : await editSbcTable(form.id, payload);
      const warn = (result.warnings || []).length ? ` Check this: ${result.warnings.join('; ')}` : '';
      setNotice(`${form.mode === 'add' ? 'Table added' : 'Table saved'}.${warn}`);
      setForm(null);
      await load();
    } catch (err) {
      const msg = errorText(err, 'Could not save this table.');
      setFormErrors(msg.split('; ').filter(Boolean));
    } finally {
      setSaving(false);
    }
  };

  const doSupersede = async (t) => {
    setSupersedeError('');
    try {
      await supersedeSbcTable(t.id, supersedeReason.trim());
      setSupersedeId(null); setSupersedeReason('');
      setNotice('Table superseded. It is kept as history and is no longer used.');
      await load();
    } catch (err) {
      setSupersedeError(errorText(err, 'Could not supersede this table.'));
    }
  };

  if (loading && data.tables.length === 0 && !loadError) {
    return <div style={{ display:'flex', justifyContent:'center', padding:'48px' }}><Spinner /></div>;
  }

  const tables = [...(data.tables || [])].sort((a, b) => (a.ends_to < b.ends_to ? 1 : -1));

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px', flexWrap:'wrap', gap:'12px' }}>
        <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:800, color:T.tx, margin:0 }}>SBC Tax Tables</h1>
        {!form && <button type="button" onClick={startAdd} style={btn(true)}>Add a table</button>}
      </div>
      <p style={{ fontSize:'13px', color:T.mt, marginBottom:'18px', maxWidth:'760px' }}>
        Small Business Corporation income-tax tables, one per range of year-ends. Preparers can only use the SBC option
        for a year-end one of these confirmed tables covers; otherwise they see a message pointing here. Add each new
        year's table from the SARS or Treasury page, with its link as the source.
      </p>

      {loadError && <div role="alert" style={{ color:T.rd, fontSize:'13px', marginBottom:'14px' }}>{loadError}</div>}
      <CoverageBanner coverage={data.coverage} onAdd={!form ? startAdd : null} />
      {data.coverage?.level === 'ok' && (
        <div data-testid="sbc-coverage-ok" style={{ fontSize:'12px', color:T.gn, marginBottom:'14px' }}>
          Covered for year-ends up to {data.coverage.covered_to}.
        </div>
      )}
      {notice && <div role="status" data-testid="sbc-notice" style={{ background:T.gnd, border:`1px solid ${T.gn}`, color:T.tx,
        borderRadius:'10px', padding:'10px 14px', fontSize:'13px', marginBottom:'14px' }}>{notice}</div>}

      {form && (
        <form onSubmit={submit} data-testid="sbc-form"
          style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'18px', marginBottom:'22px',
            display:'flex', flexDirection:'column', gap:'12px' }}>
          <div style={{ fontSize:'14px', fontWeight:700, color:T.tx }}>
            {form.mode === 'add' ? 'Add an SBC tax table' : 'Edit this SBC tax table'}
          </div>
          {form.prefilled && (
            <div style={{ fontSize:'12px', color:T.am }}>
              Pre-filled from the latest table as a starting point. Change every figure that differs on the SARS page.
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <label style={{ fontSize:'12px', color:T.mt }}>First year-end covered
              <input type="date" aria-label="First year-end covered" value={form.ends_from} style={input}
                onChange={(e) => setForm((f) => ({ ...f, ends_from: e.target.value }))} />
            </label>
            <label style={{ fontSize:'12px', color:T.mt }}>Last year-end covered
              <input type="date" aria-label="Last year-end covered" value={form.ends_to} style={input}
                onChange={(e) => setForm((f) => ({ ...f, ends_to: e.target.value }))} />
            </label>
          </div>
          <label style={{ fontSize:'12px', color:T.mt }}>Source (link to the SARS or Treasury page)
            <input type="url" aria-label="Source" value={form.source} placeholder="https://www.sars.gov.za/…" style={input}
              onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} />
          </label>

          <div>
            <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px' }}>
              Bands, as SARS prints them. "Up to" is worked out from the next band, so bands cannot leave gaps.
              Base tax is the tax SARS prints at the start of the band.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.2fr 1fr 1fr auto', gap:'8px', fontSize:'11px', color:T.mt }}>
              <span>From (R)</span><span>Up to (R)</span><span>Base tax (R)</span><span>Rate (%)</span><span />
              {form.rows.map((r, i) => (
                <FormRowCells key={i} i={i} r={r} next={form.rows[i + 1]} setRow={setRow} removeRow={removeRow} canRemove={form.rows.length > 2} />
              ))}
            </div>
            <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
              <button type="button" style={btn(false)} onClick={addRow}>Add a band</button>
              <button type="button" style={btn(false)} onClick={() => setForm((f) => ({ ...f, rows: suggestBases(f.rows) }))}>
                Fill base tax from the rates
              </button>
            </div>
          </div>

          <label style={{ fontSize:'12px', color:T.mt }}>Notes
            <input type="text" aria-label="Notes" value={form.notes} style={input}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </label>
          <label style={{ fontSize:'13px', color:T.tx, display:'flex', gap:'8px', alignItems:'flex-start' }}>
            <input type="checkbox" checked={form.confirmed} style={{ marginTop:'3px' }}
              onChange={(e) => setForm((f) => ({ ...f, confirmed: e.target.checked }))} />
            I checked every figure against the source page above.
          </label>
          {formErrors.length > 0 && (
            <ul role="alert" data-testid="sbc-form-errors" style={{ margin:0, paddingLeft:'18px', color:T.rd, fontSize:'13px' }}>
              {formErrors.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
          <div style={{ display:'flex', gap:'8px' }}>
            <button type="submit" disabled={saving} style={btn(true)}>{saving ? 'Saving…' : form.mode === 'add' ? 'Add table' : 'Save changes'}</button>
            <button type="button" style={btn(false)} onClick={() => { setForm(null); setFormErrors([]); }}>Cancel</button>
          </div>
        </form>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }} data-testid="sbc-table-list">
        {tables.length === 0 && !loadError && (
          <div style={{ color:T.mt, fontSize:'13px' }}>No SBC tables on file. Preparers cannot use the SBC option until one is added.</div>
        )}
        {tables.map((t) => {
          const live = t.status === 'confirmed';
          return (
            <div key={t.id} data-testid={`sbc-row-${t.ends_from}`}
              style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'14px 16px', opacity: live ? 1 : 0.65 }}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:700, color:T.tx, textTransform:'capitalize' }}>{t.label}</div>
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>{summariseBands(t.bands)}</div>
                </div>
                <span style={{ alignSelf:'flex-start', fontSize:'11px', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em',
                  color: live ? T.gn : T.mt, background: live ? T.gnd : T.s3, borderRadius:'6px', padding:'3px 8px' }}>
                  {live ? 'Confirmed' : 'Superseded'}
                </span>
              </div>
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'8px' }}>
                Source: <a href={t.source} target="_blank" rel="noreferrer" style={{ color:T.or }}>{t.source}</a>
                {' · '}Last changed by {t.changed_by || '—'} on {fmtDateTime(t.changed_at)}
                {t.notes ? ` · ${t.notes}` : ''}
              </div>
              <div style={{ display:'flex', gap:'8px', marginTop:'10px', flexWrap:'wrap' }}>
                {live && <button type="button" style={btn(false)} onClick={() => startEdit(t)}>Edit</button>}
                {live && <button type="button" style={btn(false)} onClick={() => { setSupersedeId(supersedeId === t.id ? null : t.id); setSupersedeReason(''); setSupersedeError(''); }}>Supersede</button>}
                <button type="button" style={btn(false)} onClick={() => setHistoryId(historyId === t.id ? null : t.id)}>
                  {historyId === t.id ? 'Hide history' : `History (${(t.edit_history || []).length})`}
                </button>
              </div>
              {supersedeId === t.id && (
                <div style={{ marginTop:'10px', display:'flex', gap:'8px', alignItems:'flex-start', flexWrap:'wrap' }}>
                  <input type="text" aria-label="Reason for superseding" placeholder="Why is this table being retired?" value={supersedeReason}
                    onChange={(e) => setSupersedeReason(e.target.value)} style={{ ...input, marginTop:0, flex:1, minWidth:'220px' }} />
                  <button type="button" style={btn(true)} onClick={() => doSupersede(t)}>Confirm supersede</button>
                  {supersedeError && <div role="alert" style={{ width:'100%', color:T.rd, fontSize:'13px' }}>{supersedeError}</div>}
                </div>
              )}
              {historyId === t.id && (
                <ul data-testid={`sbc-history-${t.ends_from}`} style={{ margin:'10px 0 0', paddingLeft:'18px', fontSize:'12px', color:T.mt }}>
                  {(t.edit_history || []).length === 0 && <li>No edits recorded (seeded by the migration).</li>}
                  {(t.edit_history || []).map((h, i) => (
                    <li key={i}>
                      {fmtDateTime(h.edited_at || h.at)} — {h.edited_by || h.by}
                      {h.action === 'created' && ': created'}
                      {h.action === 'superseded' && `: superseded (${h.reason})`}
                      {h.changed_fields && `: changed ${Object.keys(h.changed_fields).join(', ')}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {(data.conditions || []).length > 0 && (
        <div style={{ marginTop:'26px', fontSize:'12px', color:T.mt, maxWidth:'760px' }}>
          <div style={{ fontWeight:700, color:T.tx, marginBottom:'6px' }}>Eligibility conditions shown to preparers (summary)</div>
          <ul style={{ margin:0, paddingLeft:'18px' }}>{data.conditions.map((c, i) => <li key={i}>{c}</li>)}</ul>
          <div style={{ marginTop:'6px' }}>A qualified accountant should review this wording before release. KaiLux never decides eligibility; the preparer confirms it.</div>
        </div>
      )}
    </div>
  );
}

function FormRowCells({ i, r, next, setRow, removeRow, canRemove }) {
  const cell = { ...input, marginTop: 0 };
  return (
    <>
      <input aria-label={`Band ${i + 1} from`} type="number" value={r.lower} style={cell} onChange={(e) => setRow(i, 'lower', e.target.value)} />
      <input aria-label={`Band ${i + 1} up to`} value={next ? next.lower : 'and above'} readOnly style={{ ...cell, opacity: 0.6 }} />
      <input aria-label={`Band ${i + 1} base tax`} type="number" value={r.base} style={cell} onChange={(e) => setRow(i, 'base', e.target.value)} />
      <input aria-label={`Band ${i + 1} rate`} type="number" step="0.01" value={r.ratePct} style={cell} onChange={(e) => setRow(i, 'ratePct', e.target.value)} />
      <button type="button" aria-label={`Remove band ${i + 1}`} disabled={!canRemove} style={{ ...btn(false), padding:'6px 10px' }} onClick={() => removeRow(i)}>Remove</button>
    </>
  );
}
